import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BlockUIService } from 'ng-block-ui';
import { take } from 'rxjs';
import { AttachedDataLookupDto } from 'src/core/models/administration/attached-data/attached-data-lookup.dto';
import { AttachedDataDto } from 'src/core/models/administration/attached-data/attached-data.dto';
import { AttachedDataQueryItemDto } from 'src/core/models/query/attached-data-query-item.dto';
import { QueryEditorBase } from 'src/core/models/query/query-editor-base';
import { FilterItemDto } from 'src/core/models/request/filter-item.dto';
import { Operators } from 'src/core/models/request/operator.enum';
import { SorterItemDto } from 'src/core/models/request/sorter-item.dto';
import { AttachedDataService } from 'src/core/services/administration/attached-data.service';
import { CrudService } from 'src/core/services/crud/crud.service';
import { QueryService } from 'src/core/services/query/query.service';

@Component({
  selector: 'ca-query-tree-node-attached-data',
  templateUrl: './query-tree-node-attached-data.component.html',
  styles: [
    `
      :host ::ng-deep div.block-ui-wrapper {
        background-color: rgba(255, 255, 255, 0.7);
      }

      :host ::ng-deep div.block-ui-template {
        top: 50%;
      }
    `,
  ],
  styleUrls: ['./query-tree-node-attached-data.component.scss'],
  standalone: false,
})
export class QueryTreeNodeAttachedDataComponent extends QueryEditorBase implements OnInit {
  blockUiNameAttachedDataLookups: string;
  blockUiNameAttachedData: string;
  blockUiNameAttachedDataReadonly: string;

  attachedDataList: AttachedDataDto[];
  loading: boolean = true;
  multipleValuePayloads: AttachedDataQueryItemDto[] = [];
  reservedIds: number[] = [];
  reservedInternalIds: number[] = [];
  payload: AttachedDataQueryItemDto;
  form: FormGroup = null;
  currentAttachedDataName: string;
  selectedAttachedDataId: number;
  selectedAttachedDataLookupList: any[] = [];

  get selectedDatas(): any[] {
    if (this.selectedAttachedDataLookupList.length > 0 && this.payload.value) {
      const selectedIds = this.form.get('attachedDataValue').value as string[];
      return this.selectedAttachedDataLookupList.filter(item => selectedIds.includes(item.id));
    }
    return [];
  }
  get attachedDataNameList(): string {
    return this.selectedDatas.map(x => x.value).join('\r\n');
  }

  constructor(
    private service: CrudService,
    private operators: Operators,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private blockUiService: BlockUIService,
    private attachedDataService: AttachedDataService,
    private queryService: QueryService
  ) {
    super();

    this.form = formBuilder.group(
      {
        attachedDataValue: [
          null,
          {
            validators: [Validators.required],
          },
        ],
        operator: [null],
        attachedDataId: [null, Validators.required],
      },
      { updateOn: 'blur' }
    );

    this.form.statusChanges.subscribe(status => {
      this.node.validationStatus = status === 'VALID';
    });

    this.form.valueChanges.subscribe(() => {
      this.node.isDirty = this.form.dirty;
    });
  }

  async ngOnInit() {
    this.payload = this.node.categoryItem.payload as AttachedDataQueryItemDto;
    this.multipleValuePayloads = Array.isArray(this.node.categoryItem.multipleValuePayloads)
      ? this.node.categoryItem.multipleValuePayloads.map(x => x as AttachedDataQueryItemDto)
      : [];
    this.reservedIds = this.multipleValuePayloads.map(x => x.id);
    this.reservedInternalIds = this.multipleValuePayloads.map(x => x.internalId);
    this.setupBlockUiNames();

    const filters: FilterItemDto[] = [];
    const sorters: SorterItemDto[] = [];

    const blockName: string = this.isReadonly
      ? this.blockUiNameAttachedDataReadonly
      : this.blockUiNameAttachedData;
    this.blockUiService.start(blockName);

    this.attachedDataService
      .getAttachedDataList()
      .pipe(take(1))
      .subscribe({
        next: data => {
          if (this.payload.attachedDataId > 0 && data !== undefined && data.items.length > 0) {
            this.attachedDataList = data.items.filter(
              item => item.id === this.payload.attachedDataId || item.visible
            );
          } else if (
            this.payload.attachedDataId === 0 &&
            data !== undefined &&
            data.items.length > 0
          ) {
            this.attachedDataList = data.items.filter(item => item.visible);
            // set default select option
            this.payload.attachedDataId = this.attachedDataList[0].id;
          }

          if (this.payload.attachedDataId != null && this.payload.attachedDataId != 0 && data) {
            this.currentAttachedDataName = data.items.find(
              x => x.id == this.payload.attachedDataId
            ).name;
            this.selectedAttachedDataId = this.payload.attachedDataId;
            this.arrangeAttachedDataInput();
          }

          this.node.validationStatus = this.form.valid;
        },
        error: () => {},
        complete: () => {
          // finally
          this.blockUiService.stop(blockName);
        },
      });
  }

  private setupBlockUiNames() {
    this.blockUiNameAttachedDataLookups = `block-attached-data-lookups-${this.payload.internalId}`;
    this.blockUiNameAttachedData = `block-attached-data-${this.payload.internalId}`;
    this.blockUiNameAttachedDataReadonly = `block-attached-data-readonly-${this.payload.internalId}`;
  }

  get selectedAttachedData(): AttachedDataDto {
    if (this.selectedAttachedDataId != null && this.selectedAttachedDataId > 0) {
      return this.attachedDataList.find(a => a.id == this.selectedAttachedDataId);
    } else {
      return null;
    }
  }

  get isSelectedAttachedDataMultipleChoice(): boolean {
    const result = this.selectedAttachedData != null && this.selectedAttachedData.isMultipleChoice;
    return result;
  }

  attachedDataSelectionChanged(attachedDataId: number): void {
    this.selectedAttachedDataId = attachedDataId;
    this.payload.attachedDataId = attachedDataId;
    this.clearCombo();
    this.form.get('attachedDataId').patchValue(attachedDataId);
    this.arrangeAttachedDataInput();
  }

  attachedDataValueSelectionChanged(attachedDataValues: number[]): void {
    this.node.isDirty = true;

    if (!Array.isArray(attachedDataValues) || attachedDataValues.length === 0) {
      return this.clearCombo();
    }

    this.node.validationStatus = true;

    const firstId = attachedDataValues[0];
    const firstLookup = this.selectedAttachedDataLookupList.find(l => l.id === firstId);
    if (firstLookup) {
      this.payload.value = firstLookup.value;
      (this.node.categoryItem.payload as AttachedDataQueryItemDto).value = firstLookup.value;
    }
    const copyOfReservedIds = [...this.reservedIds];
    const copyOfReservedInternalIds = [...this.reservedInternalIds];
    this.multipleValuePayloads = [];

    for (let i = 1; i < attachedDataValues.length; i++) {
      const id = attachedDataValues[i];
      const reservedId = copyOfReservedIds[i - 1];
      const reservedInternalId = copyOfReservedInternalIds[i - 1];

      const newPayload = Object.assign({}, this.payload);
      newPayload.internalId = reservedInternalId ?? this.queryService.getInternalId();
      newPayload.parentInternalId = this.payload.internalId;
      newPayload.multiple = true;
      const lookup = this.selectedAttachedDataLookupList.find(l => l.id === id);
      if (lookup) {
        newPayload.value = lookup.value;
      }
      newPayload.id = reservedId ?? 0;

      this.multipleValuePayloads.push(newPayload);
    }

    this.node.categoryItem.multipleValuePayloads = [...this.multipleValuePayloads];
  }

  clearCombo() {
    this.payload.value = null;
    this.node.categoryItem.multipleValuePayloads = [];
    this.multipleValuePayloads = [];
    this.node.validationStatus = false;
    this.form.get('attachedDataValue').reset();
  }

  arrangeAttachedDataInput(): void {
    if (!this.isSelectedAttachedDataMultipleChoice) {
      this.selectedAttachedDataLookupList = [];
      this.loading = false;
      return;
    }
    this.blockUiService.start(this.blockUiNameAttachedDataLookups);
    this.attachedDataService
      .getAttachedDataLookups(this.selectedAttachedDataId)
      .pipe(take(1))
      .subscribe({
        next: response => {
          this.selectedAttachedDataLookupList = response;
          const initialIds: number[] = [];
          if (this.payload.value) {
            const first = this.selectedAttachedDataLookupList.find(
              l => l.value === this.payload.value
            );
            if (first) {
              initialIds.push(first.id);
            }
          }

          this.multipleValuePayloads.forEach(p => {
            const lookup = this.selectedAttachedDataLookupList.find(l => l.value === p.value);
            if (lookup) {
              initialIds.push(lookup.id);
            }
          });
          this.form.get('attachedDataValue').setValue(initialIds, { emitEvent: false });
        },
        error: () => {},
        complete: () => {
          // finally
          this.blockUiService.stop(this.blockUiNameAttachedDataLookups);
          this.loading = false;
        },
      });
  }
}
